/**
 * Maps Verifast government_id_checks / selfie_checks into UI checklist rows.
 */

export type VerifastCheckRequirement = "required" | "not_required" | string;

export type VerifastCheckDefinition = {
  status?: boolean | null;
  reasons?: string[];
  requirement?: VerifastCheckRequirement;
};

export type VerifastChecksSnapshot = {
  government_id_checks?: Record<string, VerifastCheckDefinition> | null;
  selfie_checks?: Record<string, VerifastCheckDefinition> | null;
  document_type?: string | null;
};

export type VerifastChecklistItem = {
  key: string;
  label: string;
  passed: boolean;
  required: boolean;
  optional: boolean;
};

export type VerifastChecklistSection = {
  title: string;
  allRequiredPassed: boolean;
  items: VerifastChecklistItem[];
};

const CHECK_LABEL_OVERRIDES: Record<string, string> = {
  id_aamva_database_lookup: "AAMVA database lookup",
  id_account_comparison: "Account comparison",
  id_age_comparison: "Age comparison",
  id_attribute_comparison: "Attribute comparison",
  id_barcode_detection: "Barcode",
  id_barcode_inconsistency_detection: "Barcode inconsistency",
  id_blur_detection: "Blur",
  id_color_inconsistency_detection: "Color",
  id_compromised_detection: "Compromised submission",
  id_damaged_detection: "Damaged detection",
  id_disallowed_country_detection: "Allowed country",
  id_disallowed_type_detection: "Allowed ID type",
  id_double_side_detection: "Double side",
  id_electronic_replica_detection: "Electronic replica",
  id_entity_detection: "Government ID",
  id_expired_detection: "Expiration",
  id_extracted_properties_detection: "Extracted properties",
  id_extraction_inconsistency_detection: "Extraction inconsistency",
  id_fabrication_detection: "Fabrication",
  id_glare_detection: "Glare",
  id_handwriting_detection: "Handwriting",
  id_inconsistent_repeat_detection: "Inconsistent repeat",
  id_inquiry_comparison: "Inquiry comparison",
  id_mrz_detection: "MRZ",
  id_mrz_inconsistency_detection: "MRZ inconsistency",
  id_portrait_clarity_detection: "Portrait clarity",
  id_portrait_detection: "Portrait",
  id_public_figure_detection: "Public figure",
  id_repeat_detection: "Repeat detection",
  id_selfie_comparison: "Selfie-to-ID comparison",
  id_tamper_detection: "Tamper detection",
  selfie_attribute_comparison: "Attribute comparison",
  selfie_id_comparison: "Selfie-to-ID comparison",
  selfie_pose_detection: "Pose position",
  selfie_pose_repeat_detection: "Pose repeat",
  selfie_multiple_faces_detection: "Multiple faces",
  selfie_account_comparison: "Account comparison",
  selfie_suspicious_entity_detection: "Suspicious entity",
  selfie_liveness_detection: "Selfie liveness",
  selfie_glasses_detection: "Glasses",
  selfie_glare_detection: "Glare",
  selfie_experimental_model_detection: "Experimental",
  selfie_portrait_quality_detection: "Portrait quality",
  selfie_public_figure_detection: "Public figure",
  selfie_age_comparison: "Age comparison",
  selfie_face_covering_detection: "Face covering",
  selfie_video_quality_detection: "Video quality",
  selfie_image_quality_detection: "Image quality",
};

function formatCheckLabel(key: string): string {
  const override = CHECK_LABEL_OVERRIDES[key];
  if (override) return override;
  const stripped = key.replace(/^(id_|selfie_)/, "").replace(/_detection$/, "");
  return stripped
    .split("_")
    .filter(Boolean)
    .map((part, index) => {
      const lower = part.toLowerCase();
      if (index > 0 && ["and", "or", "of", "to", "in"].includes(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

function isOptionalRequirement(requirement?: VerifastCheckRequirement): boolean {
  return String(requirement || "").toLowerCase() === "not_required";
}

function isRequiredRequirement(requirement?: VerifastCheckRequirement): boolean {
  return String(requirement || "").toLowerCase() === "required";
}

function checkPassed(definition: VerifastCheckDefinition): boolean {
  if (definition.status === true) return true;
  if (definition.status === false) return false;
  return isOptionalRequirement(definition.requirement);
}

export function buildChecklistSection(
  title: string,
  checks: Record<string, VerifastCheckDefinition> | null | undefined,
): VerifastChecklistSection | null {
  if (!checks || typeof checks !== "object") return null;

  const items = Object.entries(checks).map(([key, definition]) => {
    const optional = isOptionalRequirement(definition?.requirement);
    const required = isRequiredRequirement(definition?.requirement);
    return {
      key,
      label: formatCheckLabel(key),
      passed: checkPassed(definition || {}),
      required,
      optional,
    };
  });

  if (!items.length) return null;

  const allRequiredPassed = items
    .filter((item) => item.required)
    .every((item) => item.passed);

  return { title, allRequiredPassed, items };
}

export function parseVerifastChecksSnapshot(raw: unknown): VerifastChecksSnapshot | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const gov = row.government_id_checks;
  const selfie = row.selfie_checks;
  if (
    (!gov || typeof gov !== "object") &&
    (!selfie || typeof selfie !== "object")
  ) {
    return null;
  }
  return {
    government_id_checks:
      gov && typeof gov === "object"
        ? (gov as Record<string, VerifastCheckDefinition>)
        : null,
    selfie_checks:
      selfie && typeof selfie === "object"
        ? (selfie as Record<string, VerifastCheckDefinition>)
        : null,
    document_type:
      row.document_type != null ? String(row.document_type) : null,
  };
}

export function buildVerifastChecklistSections(
  snapshot: VerifastChecksSnapshot | null | undefined,
): VerifastChecklistSection[] {
  if (!snapshot) return [];
  const sections: VerifastChecklistSection[] = [];
  const idSection = buildChecklistSection(
    "ID Checks",
    snapshot.government_id_checks,
  );
  const selfieSection = buildChecklistSection(
    "Selfie Checks",
    snapshot.selfie_checks,
  );
  if (idSection) sections.push(idSection);
  if (selfieSection) sections.push(selfieSection);
  return sections;
}

export function hasVerifastChecks(snapshot: VerifastChecksSnapshot | null | undefined): boolean {
  return buildVerifastChecklistSections(snapshot).length > 0;
}
